// ============================================================
//  src/components/layout/AppLayout.js
//  App shell: sidebar (desktop) / slide-in drawer (mobile) + topbar.
//  NOTE: sidebar width (260px, --sidebar-width) and mobile topbar
//  height (58px) are relied on by InboxBotPage / InboxPickerPage.
// ============================================================
import React, { useEffect, useState } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import './AppLayout.css';

const NAV = [
  { to: '/dashboard',      icon: 'fa-house',            label: 'Dashboard'       },
  { to: '/inbox',          icon: 'fa-inbox',            label: 'Inbox'           },
  { to: '/rent-space',     icon: 'fa-store',            label: 'Rent Space'      },
  { to: '/community',      icon: 'fa-earth-africa',     label: 'Community'       },
  { to: '/collaborations', icon: 'fa-user-group',       label: 'Collaborations'  },
  { to: '/integrations',   icon: 'fa-wand-magic-sparkles', label: 'AI Integrations' },
  { to: '/billing',        icon: 'fa-credit-card',      label: 'Billing'         },
];

const NAV_BOTTOM = [
  { to: '/how-to-use',        icon: 'fa-book-open',      label: 'How to Use'        },
  { to: '/meta-verification', icon: 'fa-circle-check',   label: 'Meta Verification' },
  { to: '/settings/webhook',  icon: 'fa-link',           label: 'Webhook'           },
  { to: '/contact',           icon: 'fa-headset',        label: 'Contact Us'        },
];

const PAGE_TITLES = [...NAV, ...NAV_BOTTOM, { to: '/profile', label: 'Profile' }, { to: '/bots', label: 'Bot' }];

function LogoMark() {
  return (
    <span className="logo-icon" aria-hidden="true">
      <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
        <path d="m7 8-4 4 4 4M17 8l4 4-4 4M14 5l-4 14" />
      </svg>
    </span>
  );
}

export default function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // The Inbox wants a full-bleed, WhatsApp-Web-style layout (no page
  // gutters, fills the viewport, internal panels scroll independently).
  // Load-bearing height/overflow values are set inline on purpose.
  const isInboxRoute = location.pathname.startsWith('/inbox');
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);

  useEffect(() => {
    function onResize() { setIsMobile(window.innerWidth <= 768); }
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Close the drawer whenever the route changes
  useEffect(() => { setSidebarOpen(false); }, [location.pathname]);

  // Drawer: lock page scroll + close on Escape
  useEffect(() => {
    if (!sidebarOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = e => { if (e.key === 'Escape') setSidebarOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => { document.body.style.overflow = prev; window.removeEventListener('keydown', onKey); };
  }, [sidebarOpen]);

  // Lock document scrolling on the Inbox route so its panels scroll
  // independently instead of the whole page.
  useEffect(() => {
    if (!isInboxRoute) return undefined;
    const html = document.documentElement;
    const body = document.body;
    const prev = {
      htmlOverflow: html.style.overflow, htmlHeight: html.style.height,
      bodyOverflow: body.style.overflow, bodyHeight: body.style.height,
    };
    html.style.overflow = 'hidden';
    html.style.height = '100%';
    body.style.overflow = 'hidden';
    body.style.height = '100%';
    return () => {
      html.style.overflow = prev.htmlOverflow;
      html.style.height = prev.htmlHeight;
      body.style.overflow = prev.bodyOverflow;
      body.style.height = prev.bodyHeight;
    };
  }, [isInboxRoute]);

  const initials = `${user?.sFname?.[0] ?? ''}${user?.sLname?.[0] ?? ''}`.toUpperCase() || 'U';
  const fullName = [user?.sFname, user?.sLname].filter(Boolean).join(' ') || 'Your account';
  const pageTitle = PAGE_TITLES.find(p => location.pathname.startsWith(p.to))?.label ?? 'CommandKit';

  function handleLogout() {
    logout();
    navigate('/login');
  }

  const renderLink = ({ to, icon, label }) => (
    <NavLink
      key={to}
      to={to}
      className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}
      onClick={() => setSidebarOpen(false)}
      title={label}
    >
      <span className="nav-icon"><i className={`fa-solid ${icon}`} /></span>
      <span className="nav-label">{label}</span>
    </NavLink>
  );

  return (
    <div className={`app-shell ${sidebarOpen ? 'drawer-open' : ''}`}>
      <div
        className={`sidebar-overlay ${sidebarOpen ? 'show' : ''}`}
        onClick={() => setSidebarOpen(false)}
        aria-hidden="true"
      />

      <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`} aria-label="Main navigation">
        <div className="sidebar-logo">
          <NavLink to="/dashboard" className="logo-mark" onClick={() => setSidebarOpen(false)}>
            <LogoMark />
            <span className="logo-text">CommandKit</span>
          </NavLink>
          <button className="sidebar-close" onClick={() => setSidebarOpen(false)} aria-label="Close menu">
            <i className="fa-solid fa-xmark" />
          </button>
        </div>

        <nav className="sidebar-nav">
          <div className="nav-section-label">Workspace</div>
          {NAV.map(renderLink)}

          <div className="nav-section-label">Resources</div>
          {NAV_BOTTOM.map(renderLink)}
        </nav>

        <div className="sidebar-user">
          <NavLink
            to="/profile"
            className={({ isActive }) => `sidebar-user-link${isActive ? ' active' : ''}`}
            onClick={() => setSidebarOpen(false)}
            title="Profile"
          >
            <div className="avatar">{initials}</div>
            <div className="sidebar-user-info">
              <div className="sidebar-user-name">{fullName}</div>
              <div className="sidebar-user-email">{user?.sEmail}</div>
            </div>
          </NavLink>
          <button className="sidebar-logout" onClick={handleLogout} title="Log out" aria-label="Log out">
            <i className="fa-solid fa-arrow-right-from-bracket" />
          </button>
        </div>
      </aside>

      <div className="app-main">
        <header className="topbar">
          <button className="topbar-btn" onClick={() => setSidebarOpen(true)} aria-label="Open menu" aria-expanded={sidebarOpen}>
            <i className="fa-solid fa-bars-staggered" />
          </button>
          <NavLink to="/dashboard" className="topbar-logo">
            <LogoMark />
            <span className="topbar-title">{pageTitle === 'Dashboard' ? 'CommandKit' : pageTitle}</span>
          </NavLink>
          <NavLink to="/profile" className="topbar-avatar" aria-label="Profile">
            <div className="avatar avatar-sm">{initials}</div>
          </NavLink>
        </header>

        <div
          className={`page-content ${isInboxRoute ? 'page-content-full' : ''}`}
          style={isInboxRoute ? {
            padding: 0,
            maxWidth: 'none',
            margin: 0,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            flex: 1,
            height: isMobile ? 'calc(100vh - 58px)' : '100vh',
            overflow: 'hidden',
            boxSizing: 'border-box',
          } : undefined}
        >
          <Outlet />
        </div>
      </div>
    </div>
  );
}
